import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
  timestamp: Date
  read: boolean
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
}

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'timestamp' | 'read'>>
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        read: false,
      }

      state.notifications.unshift(notification)
      state.unreadCount++

      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50)
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload)
      if (index !== -1) {
        const notification = state.notifications[index]
        if (notification && !notification.read) {
          state.unreadCount--
        }
        state.notifications.splice(index, 1)
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload)
      if (notification && !notification.read) {
        notification.read = true
        state.unreadCount--
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true
      })
      state.unreadCount = 0
    },
    clearNotifications: (state) => {
      state.notifications = []
      state.unreadCount = 0
    },
    clearOldNotifications: (state) => {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
      state.notifications = state.notifications.filter((n) => new Date(n.timestamp) > oneDayAgo)
      state.unreadCount = state.notifications.filter((n) => !n.read).length
    },
  },
})

export const {
  addNotification,
  removeNotification,
  markAsRead,
  markAllAsRead,
  clearNotifications,
  clearOldNotifications,
} = notificationSlice.actions

export default notificationSlice.reducer

// Selectors
export const selectNotifications = (state: { notification: NotificationState }) =>
  state.notification.notifications
export const selectUnreadCount = (state: { notification: NotificationState }) =>
  state.notification.unreadCount
export const selectUnreadNotifications = (state: { notification: NotificationState }) =>
  state.notification.notifications.filter((n) => !n.read)
